import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import type { ApiResponse, User } from 'app/core/models';
import { ApiService } from 'app/core/services/api.service';
import { EncryptionService } from 'app/core/services/encryption.service';
import { UserService } from 'app/core/services/user.service';
import { STORAGE_KEYS } from 'app/core/storage/storage-prefix';
import { AuthUtils } from '../utils/auth.utils';

const TOKEN_EXPIRY_OFFSET_SECONDS = 60;

export const AUTH_ENDPOINTS = {
  LOGIN: '/Authentication/login',
  LOGIN_ADMIN: '/Authentication/loginadmin',
  CREATE_QR_CODE: '/Authentication/CreateQRCode'
} as const;

export interface SignInCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignInResult {
  success: boolean;
  /** Set on success. */
  data?: any;
  /** Already fit to show — the caller never inspects a raw error. Empty on success. */
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _apiService = inject(ApiService);
  private readonly _encryptionService = inject(EncryptionService);
  private readonly _userService = inject(UserService);

  private readonly _branchMap: Record<string, string> = {
    '01': 'HQ'
  };

  private _authenticated: boolean = false;

  set accessToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.AUTH.ACCESS_TOKEN, token);
  }

  get accessToken(): string {
    return localStorage.getItem(STORAGE_KEYS.AUTH.ACCESS_TOKEN) ?? '';
  }

  get rememberedUsername(): string {
    return this._userService.username ?? '';
  }

  /** @param storeSession `false` while an OTP challenge is pending; the caller stores it later. */
  async signIn(
    credentials: SignInCredentials,
    endpoint: string = AUTH_ENDPOINTS.LOGIN,
    storeSession: boolean = true
  ): Promise<SignInResult> {
    const response = await this._apiService.post<ApiResponse<any>>(endpoint, {
      Username: credentials.username,
      Password: credentials.password
    });

    // A response without a token is a rejected sign-in even when the call itself succeeded.
    if (!response.success || !response.data?.token) {
      return {
        success: false,
        message: response.message || 'Incorrect employee code or password'
      };
    }

    if (storeSession) {
      this.setAuthenticatedSession(response.data, credentials);
    }

    return { success: true, data: response.data, message: '' };
  }

  /** Shared by all three ways in: password, password plus OTP, and a confirmed QR scan. */
  setAuthenticatedSession(authData: any, credentials?: Pick<SignInCredentials, 'username' | 'rememberMe'>): void {
    this.accessToken = authData.token;
    this._authenticated = true;

    const user = this._buildUser(authData);

    this._userService.user = user;
    this._userService.userInfo = user;

    // Outlives the session on purpose: once the token expires the role is gone, and that is
    // exactly when we need to know which of the two sign-in pages to send the user back to.
    localStorage.setItem(
      STORAGE_KEYS.AUTH.SIGN_IN_PATH,
      this._encryptionService.encryptAES256(this.signInPathForRole(user.role))
    );

    if (credentials?.rememberMe && credentials.username) {
      this._userService.username = credentials.username;
    } else {
      localStorage.removeItem(STORAGE_KEYS.AUTH.USERNAME);
    }
  }

  signInUsingToken(): Observable<any> {
    const user = this._userService.userInfo;

    if (!user) {
      this._clearAuthState();
      return throwError(() => 'User info not found.');
    }

    this._authenticated = true;
    this._userService.user = user;

    return of(true);
  }

  signOut(): Observable<any> {
    this._clearAuthState();
    return of(true);
  }

  signInPathForRole(role?: string | null): string {
    return role === 'Admin' ? '/sign-in-admin' : '/sign-in';
  }

  /**
   * Where a session that just ended goes. An admin dropped on the staff page types the right
   * credentials into the wrong form and cannot get in, so we return to the page they signed in on.
   * `expired` only rides along when this browser had a session to lose — a first-time visitor
   * bounced off a guarded url is not told that theirs expired.
   */
  signInUrlForEndedSession(redirectUrl: string): string {
    const signInPath = this._storedSignInPath();
    const expired = signInPath ? '&expired=1' : '';

    return `${signInPath ?? '/sign-in'}?redirectURL=${encodeURIComponent(redirectUrl)}${expired}`;
  }

  check(): Observable<boolean> {
    if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken, TOKEN_EXPIRY_OFFSET_SECONDS)) {
      this._clearAuthState();
      return of(false);
    }

    if (this._authenticated) {
      return of(true);
    }

    return this.signInUsingToken().pipe(
      catchError(() => of(false))
    );
  }

  /**
   * Stored encrypted like the rest of the auth keys. Decryption is the one place a tampered or
   * stale value can reach: it throws, and this runs inside the guard, so a bad value would strand
   * the user on a blank page instead of the sign-in form it exists to pick.
   */
  private _storedSignInPath(): string | null {
    try {
      return this._encryptionService.decryptAES256(localStorage.getItem(STORAGE_KEYS.AUTH.SIGN_IN_PATH));
    } catch {
      return null;
    }
  }

  private _clearAuthState(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.AUTH.USER_INFO);

    this._authenticated = false;
    this._userService.user = null;
    this._userService.userInfo = null;
  }

  private _mapBranchCode(branchCode?: string | null): string {
    return this._branchMap[branchCode ?? ''] ?? '';
  }

  private _buildUser(data: any): User {
    return {
      employeeCode: data.employeeCode ?? '',
      name: `${data.firstname ?? ''} ${data.lastname ?? ''}`.trim(),
      nameEng: `${data.firstnameEng ?? ''} ${data.lastnameEng ?? ''}`.trim(),
      email: data.email ?? '',
      position: data.loginPosition ?? '',
      branchCode: this._mapBranchCode(data.branchCode),
      branchName: data.branchName ?? '',
      role: data.role ?? '',
      avatar: data.profilePicture ? `data:image/png;base64,${data.profilePicture}` : null
    };
  }

}
